/**
 * PsychologicalProgressBar — ease-out progress illusion.
 *
 * Technique from UX research: fast start (rushes to ~70% quickly),
 * then decelerates asymptotically. Same perceived speed, different feeling.
 * Never reaches 100% until `done` is set — then animates to 100%.
 *
 * Usage:
 *   const progress = useProgressIllusion({ active: isLoading, tau: 15 });
 *   <PsychologicalProgressBar progress={progress} color="red" />
 */

import { useState, useEffect, useRef } from "react";

// ── Hook ──────────────────────────────────────────────────────────────────────

interface ProgressIllusionOptions {
  /** Whether loading is active. Starts/resets the timer. */
  active: boolean;
  /**
   * Time constant in seconds. Smaller = faster rush to plateau.
   * - 8–12s: graphics generation (~15–45s real time)
   * - 18–25s: transcription (~30–120s real time)
   */
  tau?: number;
  /** Max progress before completion (0–1). Default 0.92. */
  maxBeforeDone?: number;
  /** Tick interval in ms. Default 200. */
  tickMs?: number;
}

/**
 * Returns a progress value 0–1.
 * While active: rises via ease-out curve, capped at maxBeforeDone.
 * When !active (done): instantly returns 1.
 */
export function useProgressIllusion({
  active,
  tau = 15,
  maxBeforeDone = 0.92,
  tickMs = 200,
}: ProgressIllusionOptions): number {
  const startRef = useRef<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      startRef.current = null;
      setElapsed(0);
      return;
    }

    startRef.current = Date.now();
    setElapsed(0);

    const interval = setInterval(() => {
      if (startRef.current === null) return;
      setElapsed((Date.now() - startRef.current) / 1000);
    }, tickMs);

    return () => clearInterval(interval);
  }, [active, tickMs]);

  if (!active) return 0;

  // Exponential ease-out: f(t) = maxBeforeDone * (1 - e^(-t/tau))
  const raw = maxBeforeDone * (1 - Math.exp(-elapsed / tau));
  return Math.min(raw, maxBeforeDone);
}

// ── Component ─────────────────────────────────────────────────────────────────

type ProgressColor = "red" | "blue" | "purple" | "cyan" | "amber";

const COLOR_MAP: Record<ProgressColor, string> = {
  red: "from-red-500 to-rose-400",
  blue: "from-blue-500 to-cyan-400",
  purple: "from-purple-500 to-violet-400",
  cyan: "from-cyan-500 to-teal-400",
  amber: "from-amber-500 to-yellow-400",
};

interface PsychologicalProgressBarProps {
  /** 0–1 progress value. */
  progress: number;
  color?: ProgressColor;
  /** Height class. Default 'h-1'. */
  heightClass?: string;
  className?: string;
  /** Show shimmer animation on the track. */
  shimmer?: boolean;
}

export function PsychologicalProgressBar({
  progress,
  color = "blue",
  heightClass = "h-1",
  className = "",
  shimmer = true,
}: PsychologicalProgressBarProps) {
  const pct = `${Math.round(progress * 100)}%`;
  const gradient = COLOR_MAP[color];

  return (
    <div
      className={`w-full ${heightClass} bg-white/[0.06] rounded-full overflow-hidden relative ${className}`}
    >
      {/* Shimmer on track */}
      {shimmer && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent animate-[shimmer_2s_linear_infinite]" />
      )}
      {/* Progress fill */}
      <div
        className={`h-full bg-gradient-to-r ${gradient} rounded-full`}
        style={{
          width: pct,
          transition: "width 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        }}
      />
    </div>
  );
}

// ── Skeleton shimmer block ────────────────────────────────────────────────────

interface ShimmerBlockProps {
  className?: string;
}

/** A shimmering placeholder block for skeleton screens. */
export function ShimmerBlock({ className = "" }: ShimmerBlockProps) {
  return (
    <div className={`bg-white/[0.06] rounded relative overflow-hidden ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.09] to-transparent animate-[shimmer_1.8s_linear_infinite]" />
    </div>
  );
}
